<template>
  <div>
    <!-- Filtros -->
    <activity-filters :projectId="project.id"
      v-show="depth == 0"
      @filters-updated="filtersUpdated"
      ref="filtersRef"/>

    <div class="flex w-full justify-end" v-show="depth == 0">
      <!-- Botones, sólo Jefes de departamento -->
      <quick-button-menu :buttons="quickButtonMenu()">
        <template v-if="project.can_admin || true"> <!-- always show -->
          <project-report v-if="!isLoadingTable && !project.can_collaborate" :filters="this.generatedFilters" :openWithButton="false"/>
          <project-report-excel v-if="showExcelReport && !project.can_collaborate" :openWithButton="false"/>
        </template>
        <!-- Botón Lotes canal plan sanea exportar EXCEL proyecto -->
        <project-report-sanea v-if="showSaneaReport"/>
        <!-- Botón Lotes canal plan red exportar CSV proyecto -->
        <project-report-lotes v-if="showLotesReport" :openWithButton="false"/>
      </quick-button-menu>
    </div>

    <div class="panel relative"
    :style="{ 'background-color' : color ? color : '#ffffff' , 'padding' : depth == 0 ? '1rem' : '3px' }">
      <!-- Selector del periodo planificado mostrado en la columna "Progreso Programado".
           Solo en la raíz (depth==0): los subniveles heredan el periodo del padre. -->
      <div v-if="depth == 0" class="flex items-center justify-between mb-3 text-xs">
        <div class="flex items-center">
          <span class="text-grey-darker font-medium mr-2">{{ $t('project_progress_total') }}:</span>
          <progress-circle :progress="totalProjectProgressClamped" class="mr-2" />

          <template v-if="project.can_progress">
            <span class="ml-4 text-grey-darker font-medium mr-2">{{ $t('actual_progress') }}:</span>
            <progress-circle :progress="totalRealProgressClamped" color="green" class="mr-2" />
          </template>

          <span class="ml-4 text-grey-darker font-medium mr-2">{{ $t('scheduled_progress') }}:</span>
          <progress-circle :progress="totalScheduledProgressClamped" color="orange" class="mr-2" />
        </div>

        <!-- Estado de tarea del proyecto: indicador WeStop (sale del proyecto, igual que en activities.vue) -->
        <b-tooltip v-if="project.has_westop"
          :label="project.has_westop ? 'We Stop project' : 'We Stop Activity'" position="is-top">
          <img :src="asset('public/westop-tke.png')" alt="Expanded Image" width="50" class="p-4" />
        </b-tooltip>

        <div class="flex flex-col items-end">
          <div class="flex items-center">
            <span class="mr-2 text-grey-darker">{{ scheduledPeriodLabel }}:</span>
            <b-field class="mb-0">
              <p class="control">
                <button type="button"
                        class="button is-small"
                        :class="effectiveScheduledPeriod === 'today' ? 'is-info is-selected' : ''"
                        @click="onScheduledPeriodChange('today')">
                  {{ effectiveScheduledMode === 'delta' ? $t('today') : $t('progress_origin') }}
                </button>
              </p>
              <p class="control">
                <button type="button"
                        class="button is-small"
                        :class="effectiveScheduledPeriod === 'week' ? 'is-info is-selected' : ''"
                        @click="onScheduledPeriodChange('week')">
                  {{ $t('this_week') }}
                </button>
              </p>
              <p class="control">
                <button type="button"
                        class="button is-small"
                        :class="effectiveScheduledPeriod === 'month' ? 'is-info is-selected' : ''"
                        @click="onScheduledPeriodChange('month')">
                  {{ $t('this_month') }}
                </button>
              </p>
            </b-field>
          </div>

          <div class="flex items-center mt-1">
            <span class="mr-2 text-grey-darker">{{ $t('scheduled_progress_mode') }}:</span>
            <b-field class="mb-0">
              <p class="control">
                <button type="button"
                        class="button is-small"
                        :class="effectiveScheduledMode === 'accumulated' ? 'is-info is-selected' : ''"
                        @click="onScheduledModeChange('accumulated')">
                  {{ $t('progress_origin') }}
                </button>
              </p>
              <p class="control">
                <button type="button"
                        class="button is-small"
                        :class="effectiveScheduledMode === 'delta' ? 'is-info is-selected' : ''"
                        @click="onScheduledModeChange('delta')">
                  {{ $t('progress_partial') }}
                </button>
              </p>
            </b-field>
          </div>
        </div>
      </div>

      <section>
        <b-table
          ref="tbl_groups"
          :data="listGroups"
          :paginated="false"
          :loading="isLoadingTable"
          :striped="true"
          :hoverable="true"
          :bordered="true"
          :mobile-cards="false"
          @row-click="onClick"
          icon-pack="fas"
          class="text-xs border-blue-lighter"
          detailed
          detail-key="id"
          :opened-detailed="tableOpenedDetailedIds"
          :detail-transition="'fade'"
          @details-open="onGroupDetailsOpen"
          @details-close="onGroupDetailsClose"
          >
          <template slot="empty">
            <div v-if="! isLoadingTable" class="m-4 text-lg text-dark-grey">
              <span>{{ $t('no_results') }}</span>
            </div>
          </template>
          <template>
             <b-table-column field="color" :label="$t('color')" v-slot="group">
               <span class="icon rounded-full mr-2"
                :style="{ 'background-color' : group.row.color ? group.row.color : 'initial', 'color' : group.row.color ? 'white' : 'initial' }">
                <b-icon pack="fas" icon="layer-group" size="is-small" />
              </span>
             </b-table-column>
              <b-table-column field="code" :label="$t('code')" sortable :custom-sort="sortByCode" v-slot="group">
                {{ group.row.code }}
              </b-table-column>

              <b-table-column field="name" :label="$t('name')" sortable v-slot="group">
                {{ group.row.name }}
              </b-table-column>
              <b-table-column field="weight" :label="$t('weight')" sortable v-slot="group">
                {{ formatWeight(group.row.weight) }} %
              </b-table-column>
              <b-table-column field="progress" :label="progressLabel" sortable v-slot="group">
                <progress-bar
                  :step="currentProjectProgress(group.row.group_progress)"
                  :max="group.row.weight"
                  :onlyStep="true"
                  :decimals="displayDecimals"
                  color="blue-light"
                  class="w-full"
                />
              </b-table-column>
              <b-table-column field="progress_group" :label="$t('group') + ' ' + $t('progress_label')" sortable v-slot="group">
                <progress-bar
                  :step="currentGroupProgress(group.row.group_progress)"
                  :decimals="displayDecimals"
                  color="blue-light"
                  class="w-full"
                />
              </b-table-column>
              <b-table-column v-if="project.can_progress" field="actual_progress" :label="progressActualLabel" sortable v-slot="group">
                <progress-bar
                :step="realProjectProgress(group.row.group_progress)"
                :decimals="displayDecimals"
                color="green"
                class="w-full"
                />
              </b-table-column>
              <b-table-column v-if="project.can_progress" field="actual_progress_group" :label="$t('group') + ' ' + $t('actual_progress')" sortable v-slot="group">
                <progress-bar
                :step="realGroupProgress(group.row.group_progress)"
                :decimals="displayDecimals"
                color="green"
                class="w-full"
                />
              </b-table-column>
              <b-table-column field="scheduled_progress_group" :label="$t('scheduled') + ' ' + $t('progress_label')" sortable v-slot="group">
                <progress-bar
                  :step="scheduledGroupProgress(group.row.group_progress)"
                  :decimals="displayDecimals"
                  color="orange"
                  class="w-full"
                />
              </b-table-column>

              <b-table-column field="task_status" :label="$t('task_status')"
                :visible="company && company.configs && company.configs.subtasks == true"
                centered v-slot="group">
                <div v-if="getIssueIcon(group.row.task_status) != null"
                  class="flex text-center align-middle cursor-pointer justify-center ">
                  <b-tooltip class="text-center" :label="$t('issues')" position="is-left">
                    <b-icon v-if="getIssueIcon(group.row.task_status).icon != 'hand'"
                      :icon="getIssueIcon(group.row.task_status).icon" pack="fas" size="is-small" :class="getIssueIcon(group.row.task_status).color"></b-icon>
                    <img v-else :src="asset('public/westop-tke.png')" alt="Expanded Image" width="40" class="p-1" />
                  </b-tooltip>
                </div>
              </b-table-column>

          </template>

          <template slot="detail" slot-scope="group">
            <!-- Lazy loading: si el grupo tiene subgrupos (children_count > 0) renderizamos una sub-instancia
                 que se monta y pide su propio nivel al backend. Si no, mostramos directamente las actividades. -->
            <div v-if="group.row.children_count > 0">
              <activity-groups
                :parent-id="group.row.id"
                :depth="depth + 1"
                :color="group.row.color"
                :filters-from-root="generatedFilters"
                :key="group.row.id"
                :groups-detail-path-shared="effectiveGroupsPath"
                :scheduled-period-showed="effectiveScheduledPeriod"
                :scheduled-mode-showed="effectiveScheduledMode"
                @groups-detail-path-change="onChildGroupsDetailPathChange"
                />

            </div>
            <div v-else :style="{ 'background-color' : group.row.color ? group.row.color : '#ffffff' }">
              <activities
                :filtersProps="getActivityFilters(group.row)"
                :groupName="group.row.name"
                :groupColor="group.row.color"
                :userColumnsConfig="userColumnsConfig"
                :key="group.row.id"
                />
            </div>
          </template>

        </b-table>

    </section>
  </div>
  </div>
</template>

<script>
import Vue from "vue"
import axios from 'axios'
import {mapGetters} from 'vuex'
import Form from 'vform'
import projectReport from '·/components/project-report'
import projectReportExcel from '·/components/project-report-excel'
import projectReportLotes from '·/components/project-report-lotes'
import ButtonCircle from '../../components/buttons-menu/button-circle.vue';
import moment from 'moment'
import Buefy from 'buefy'
import BIcon from "buefy/src/components/icon/Icon";
import EventBus from '~/plugins/bus'
import ActivityFilters from '·/components/activity-filters';
import QuickButtonMenu from '../../components/quick-button-menu/quick-button-menu.vue';
import Activities from '../activity/groups/activities.vue';
import ProgressCircle from './reports/detailed/components/progress-circle.vue';
import { formatProjectFigure, quantityDisplayDecimals } from '·/helpers/quantity-display';

(moment);
Vue.use(Buefy);

export default {
  name: 'activity-groups',
  middleware: 'auth',
  title: 'activities_page_title',
  subtitle: 'activities_page_subtitle_by_groups',
  menuOption: '2-2',

  components: {
    BIcon,
    projectReport,
    projectReportExcel,
    projectReportLotes,
    Activities,
    ActivityFilters,
    ButtonCircle,
    QuickButtonMenu,
    ProgressCircle,
  },

  computed: {
    ...mapGetters({
      user: 'auth/user',
      project: 'app/project',
      company: 'app/company',
    }),

    filters() {
      return this.$refs.filtersRef.generateFilters()
    },

    progressLabel() {
      return this.depth > 0 ? this.$t('progress_relative_to_parent') : this.$t('project') + ' ' + this.$t('progress_label')
    },

    progressActualLabel() {
      return this.depth > 0 ? this.$t('progress_actual_relative_to_parent') : this.$t('project') + ' ' + this.$t('actual_progress')
    },

    displayDecimals() {
      return quantityDisplayDecimals(this.project)
    },

    effectiveGroupsPath() {
      return this.groupsDetailPathShared != null ? this.groupsDetailPathShared : this.localGroupsDetailPath
    },

    tableOpenedDetailedIds() {
      const id = this.effectiveGroupsPath[this.depth]
      if (id === undefined || id === null || id === '') {
        return []
      }
      const ok = this.listGroups.some((g) => String(g.id) === String(id))
      return ok ? [id] : []
    },

    effectiveScheduledPeriod() {
      if (this.scheduledPeriodShowed != null && this.scheduledPeriodShowed !== '') {
        return this.scheduledPeriodShowed
      }
      return this.scheduledPeriod
    },

    effectiveScheduledMode() {
      if (this.scheduledModeShowed != null && this.scheduledModeShowed !== '') {
        return this.scheduledModeShowed
      }
      return this.scheduledMode
    },

    scheduledPeriodLabel() {
      return this.effectiveScheduledMode === 'delta'
        ? this.$t('scheduled_progress_during')
        : this.$t('scheduled_progress_at_close_of')
    },

    // para grafiquito redondo de progreso proyecto
    totalProjectProgress() {
      if (!this.listGroups || !this.listGroups.length) {
        return 0
      }

      return this.listGroups.reduce((sum, group) => {
        const gp = group.group_progress || {}
        return sum + (parseFloat(gp.progress_current_project) || 0)
      }, 0)
    },

    totalProjectProgressClamped() {
      const value = Math.min(100, Math.max(0, this.totalProjectProgress))
      return this.roundDisplayed(value)
    },

    // Avance real total del proyecto: suma de los reales ponderados de los grupos raíz.
    totalRealProgress() {
      if (!this.listGroups || !this.listGroups.length) {
        return 0
      }

      return this.listGroups.reduce((sum, group) => {
        const gp = group.group_progress || {}
        return sum + (parseFloat(gp.progress_real_project) || 0)
      }, 0)
    },

    totalRealProgressClamped() {
      const value = Math.min(100, Math.max(0, this.totalRealProgress))
      return this.roundDisplayed(value)
    },

    // Progreso planificado total del proyecto acumulado a HOY. Fijo — no depende
    // de los toggles período/modo, así el par (real, planificado) queda comparable
    // a simple vista: "estoy en X% real vs Y% que debería tener hoy".
    totalScheduledProgress() {
      if (!this.listGroups || !this.listGroups.length) {
        return 0
      }

      return this.listGroups.reduce((sum, group) => {
        const gp = group.group_progress || {}
        return sum + (parseFloat(gp.progress_scheduled_project) || 0)
      }, 0)
    },

    totalScheduledProgressClamped() {
      const value = Math.min(100, Math.max(0, this.totalScheduledProgress))
      return this.roundDisplayed(value)
    },
  },

  props: {
    depth: { type: Number, default: 0 },
    color: { type: String, default: '#ffffff' },
    /** Id del grupo padre cuyos hijos directos debe cargar esta instancia. null = nivel raíz. */
    parentId: { type: Number, default: null },
    /** Filtros generados por la raíz (filtros de usuario + paginación + with_groups). Los hijos los
        reutilizan tal cual para que la restricción de visibilidad sea coherente en todos los niveles. */
    filtersFromRoot: { type: Object, default: null },
    /** Ruta de grupos con detalle abierto (mismo array en todos los niveles; null solo en raíz antes de compartir). */
    groupsDetailPathShared: { type: Array, default: null },
    /** Periodo planificado mostrado en la columna (today / week / month). La raíz lo controla; los subniveles lo heredan. */
    scheduledPeriodShowed: { type: String, default: null },
    scheduledModeShowed: { type: String, default: null },
  },

  data: () => ({
    form: new Form({
      creator: 'Todos',
      fecha: '1 Enero 2018 - 31 Diciembre 2018',
    }),
    hasGroups: false,
    // paginationRows: 10,
    isLoadingTable: true,
    page: 1,
    last: 1,
    perPage: 25,
    currentPage: 1,
    total: 0,
    orderField: "planning_code",
    orderDir: "asc",
    listGroups: [],
    filterSubactivities: false,
    showSubs: false,
    openedSubactivitiesId: 0,
    subactivities: [],
    showFilters: false,
    generatedFilters: {}, // objeto con los filtros generados por el componente 'activity-filters'
    groupsAssignmentRefreshTimer: null,
    columns: {
      'in_planning': {title: 'in_planning', visible: true},
      'user': {title: 'manager', visible: true},
      'planning_code': {title: 'code', visible: true},
      'name': {title: 'name', visible: true},
      'progress': {title: 'progress', visible: true},
      'current_start_date': {title: 'start_date', visible: true},
      'current_end_date': {title: 'end_date', visible: true},
      'status': {title: 'status', visible: true},
      'status_id': {title: 'custom_status', visible: true},
      'notifications': {title: 'notifications', visible: true},
    },
    showExcelReport: false,
    showLotesReport: false,
    /** Camino de ids de grupo con fila detailed abierta (solo se persiste en profundidad 0). */
    localGroupsDetailPath: [],
    childStatusList: [], // lista status de componente activity-filters
    apiProject: {}, // algunas caracteristicas del proyecto actual devueltas por api (si user es certificador)
    userColumnsConfig: null, // config de columnas guardada por el usuario
    scheduledPeriod: 'today',
    scheduledMode: 'accumulated',
  }),

  mounted() {

    if (this.depth === 0) {
      this.restoreGroupsDetailPathFromSession()
      this.restoreScheduledPeriodFromSession()
      this.restoreScheduledModeFromSession()
    }

    // recupera de la localstore la pagina que estamos (util al volver de una validacion al listado)
    this.page = this.$store.getters["searchFilters/activities"].page || 1
    this.last = this.$store.getters["searchFilters/activities"].last || 1

    this.applyFilters(this.page);

    // Carga la config de columnas del usuario 
    axios.get(`/api/v2/user/${this.user.id}/configs`, { params: { project_id: this.project.id } })
      .then(({ data }) => { this.userColumnsConfig = (data && data.activities_columns) ? data.activities_columns : null })
      .catch(() => {});

    let self = this;

    // Muestra columna "mis estados" cuando el componente hijo 'activity-filters' recibe por api lista statusList
    EventBus.$on("statusListReceived", function (statusList) {
      self.childStatusList = statusList
    });

    // Si recibimos el evento refresh, refrescamos el componente
    EventBus.$on('refreshActivitiesNotifications', function (e) {
      self.isLoading = true;
      self.applyFilters(self.page);
    });

    // Si recibimos el evento refresh, refrescamos el componente
    EventBus.$on('refreshSubactivitiesNotifications', function (e) {
      self.isLoading = true;
      self.applyFilters(self.page);
    });

    // Tras registro de progreso (propio u otro usuario): refrescar grupos.
    // El % de grupo persistido puede llegar un poco después (job); ActivityGroupProgressRecalculated
    // vuelve a disparar refreshActivitiesNotifications cuando el recálculo termina.
    const scheduleGroupsRefresh = () => {
      if (self.groupsAssignmentRefreshTimer) {
        clearTimeout(self.groupsAssignmentRefreshTimer)
      }
      self.groupsAssignmentRefreshTimer = setTimeout(() => {
        self.groupsAssignmentRefreshTimer = null
        self.applyFilters(self.page)
      }, 150)
    }
    EventBus.$on('refreshAssignmentChangedNotifications', scheduleGroupsRefresh);
    EventBus.$on('refreshAssignmentCreatedNotifications', scheduleGroupsRefresh);
    EventBus.$on('refreshAssignmentDeletedNotifications', scheduleGroupsRefresh);
  },

  beforeDestroy(){
    EventBus.$off("statusListReceived");
    EventBus.$off("refreshActivitiesNotifications");
    EventBus.$off("refreshSubactivitiesNotifications");
    EventBus.$off("refreshAssignmentChangedNotifications");
    EventBus.$off("refreshAssignmentCreatedNotifications");
    EventBus.$off("refreshAssignmentDeletedNotifications");
    if (this.groupsAssignmentRefreshTimer) {
      clearTimeout(this.groupsAssignmentRefreshTimer)
      this.groupsAssignmentRefreshTimer = null
    }
  },

  created() {
    this.showExcelReport = (
      window.location.hostname.includes('test') ||
      this.user.company.id == 16 ||
      this.company.alias == 'tsk' ||
      this.company.alias == 'x-elio' ||
      this.company.alias == 'welink' ||
      this.company.alias == 'tci' ||
      this.company.alias == 'eag' ||
      this.company.alias == 'ohla-mexico'
    )
      ? true : false

    this.showLotesReport = this.company.configs && this.company.configs.loteCanal
    this.showSaneaReport = this.project.configs && this.project.configs.is_plan_sanea === true

  },

  methods: {
    formatWeight(weight) {
      const formatted = formatProjectFigure(weight, this.project)
      return formatted === '' ? formatProjectFigure(0, this.project) : formatted
    },

    roundDisplayed(value) {
      const factor = this.displayDecimals === 4 ? 10000 : 100
      return Math.round(value * factor) / factor
    },

    activityGroupsDetailSessionKey() {
      if (!this.project || !this.project.id) {
        return null
      }
      return 'epc_activity_groups_detail_' + this.project.id
    },

    persistGroupsDetailPath() {
      if (this.depth !== 0) {
        return
      }
      try {
        const key = this.activityGroupsDetailSessionKey()
        if (!key) {
          return
        }
        sessionStorage.setItem(key, JSON.stringify({ path: this.localGroupsDetailPath }))
      } catch (e) {
        // ignorar quota / modo privado
      }
    },

    restoreGroupsDetailPathFromSession() {
      if (this.depth !== 0) {
        return
      }
      try {
        const key = this.activityGroupsDetailSessionKey()
        if (!key) {
          return
        }
        const raw = sessionStorage.getItem(key)
        if (!raw) {
          return
        }
        const parsed = JSON.parse(raw)
        const path = parsed && Array.isArray(parsed.path) ? parsed.path : []
        this.localGroupsDetailPath = path.filter((id) => id !== '' && id !== null && id !== undefined)
      } catch (e) {
        // JSON inválido u otro error
      }
    },

    clearGroupsDetailUiState() {
      this.localGroupsDetailPath = []
      try {
        const key = this.activityGroupsDetailSessionKey()
        if (key) {
          sessionStorage.removeItem(key)
        }
      } catch (e) {
        // ignorar
      }
    },

    onChildGroupsDetailPathChange(newPath) {
      if (this.depth === 0) {
        this.localGroupsDetailPath = newPath.slice()
        this.persistGroupsDetailPath()
      } else {
        this.$emit('groups-detail-path-change', newPath)
      }
    },

    onGroupDetailsOpen(row) {
      const newPath = this.effectiveGroupsPath.slice(0, this.depth).concat([row.id])
      this.onChildGroupsDetailPathChange(newPath)
    },

    onGroupDetailsClose(row) {
      const cur = this.effectiveGroupsPath[this.depth]
      if (cur === undefined || cur === null) {
        return
      }
      if (String(cur) !== String(row.id)) {
        return
      }
      const newPath = this.effectiveGroupsPath.slice(0, this.depth)
      this.onChildGroupsDetailPathChange(newPath)
    },

    async getActivityGroupsList(args = {}) {
      // Lazy loading: cada instancia (raíz o sub-nivel) pide su propio nivel al backend.
      // - Raíz: sin parent_id, devuelve grupos raíz del proyecto.
      // - Sub-nivel: parent_id = id del grupo padre, devuelve solo sus hijos directos.
      this.isLoadingTable = true

      const params = { ...args }
      if (this.parentId !== null && this.parentId !== undefined) {
        params.parent_id = this.parentId
      }

      const {data} = await axios.get(`/api/v2/project/${this.project.id}/activity/groups`, {params});

      // Solo la raíz mantiene paginación / estado a nivel proyecto.
      if (this.depth === 0) {
        this.last = data.lastPage;
        this.total = data.total;
        this.apiProject = data.project;
        this.$store.dispatch('searchFilters/setActivitiesFilters', { last: this.last });
      }

      if (data && data.groups) {
        // La columna kanban solo se evalúa en la raíz (es la única que tiene activity-filters).
        if (this.depth === 0 && !this.hasStatusList()) {
          delete this.columns.status_id;
        }

        this.hasGroups = data.groups.length > 0
        // Los sub-niveles los mostramos ordenados por código (igual que hacía el template antes con sortByCode).
        // La raíz mantiene el orden devuelto por el backend (order_column).
        this.listGroups = this.depth === 0 ? data.groups : this.sortByCode(data.groups);
      } else {
        this.$notify.error('error_loading_activities')
      }

      this.isLoadingTable = false
    },


    filtersUpdated() {
      if (this.depth === 0) {
        this.clearGroupsDetailUiState()
      }
      this.isLoadingTable = true;
      this.applyFilters(1);
    },

    onScheduledPeriodChange(value) {
      if (!['today', 'week', 'month'].includes(value)) {
        return
      }
      if (this.effectiveScheduledPeriod === value) {
        return
      }
      this.scheduledPeriod = value
      this.persistScheduledPeriod()
    },

    scheduledPeriodSessionKey() {
      if (!this.project || !this.project.id) {
        return null
      }
      return 'epc_activity_groups_scheduled_period_' + this.project.id
    },

    persistScheduledPeriod() {
      if (this.depth !== 0) {
        return
      }
      try {
        const key = this.scheduledPeriodSessionKey()
        if (!key) {
          return
        }
        sessionStorage.setItem(key, this.scheduledPeriod)
      } catch (e) {
        // ignorar quota / modo privado
      }
    },

    restoreScheduledPeriodFromSession() {
      if (this.depth !== 0) {
        return
      }
      try {
        const key = this.scheduledPeriodSessionKey()
        if (!key) {
          return
        }
        const stored = sessionStorage.getItem(key)
        if (stored && ['today', 'week', 'month'].includes(stored)) {
          this.scheduledPeriod = stored
        }
      } catch (e) {
        // ignorar
      }
    },

    onScheduledModeChange(value) {
      if (!['accumulated', 'delta'].includes(value)) {
        return
      }
      if (this.effectiveScheduledMode === value) {
        return
      }
      this.scheduledMode = value
      this.persistScheduledMode()
    },

    scheduledModeSessionKey() {
      if (!this.project || !this.project.id) {
        return null
      }
      return 'epc_activity_groups_scheduled_mode_' + this.project.id
    },

    persistScheduledMode() {
      if (this.depth !== 0) {
        return
      }
      try {
        const key = this.scheduledModeSessionKey()
        if (!key) {
          return
        }
        sessionStorage.setItem(key, this.scheduledMode)
      } catch (e) {
        // ignorar quota / modo privado
      }
    },

    restoreScheduledModeFromSession() {
      if (this.depth !== 0) {
        return
      }
      try {
        const key = this.scheduledModeSessionKey()
        if (!key) {
          return
        }
        const stored = sessionStorage.getItem(key)
        if (stored && ['accumulated', 'delta'].includes(stored)) {
          this.scheduledMode = stored
        }
      } catch (e) {
        // ignorar
      }
    },

    scheduledGroupProgress(groupProgress) {
      const gp = groupProgress || {}
      const fieldByPeriodAndMode = {
        accumulated: {
          today: 'progress_scheduled_group',
          week:  'progress_scheduled_group_week',
          month: 'progress_scheduled_group_month',
        },
        delta: {
          today: 'progress_scheduled_group_today_delta',
          week:  'progress_scheduled_group_week_delta',
          month: 'progress_scheduled_group_month_delta',
        },
      }
      const modeMap = fieldByPeriodAndMode[this.effectiveScheduledMode] || fieldByPeriodAndMode.accumulated
      const field = modeMap[this.effectiveScheduledPeriod] || modeMap.today
      // Clamp a 0 en modo delta: un delta negativo (re-planificación que mueve trabajo
      // del futuro al pasado) es información válida pero confusa en una barra 0-100.
      const value = parseFloat(gp[field]) || 0
      return this.effectiveScheduledMode === 'delta' ? Math.max(0, value) : value
    },

    // A origen: acumulado normal ponderado al peso del grupo. Parcial: avance normal del
    // período en curso (progress ahora − baseline today/week/month de progress_periods).
    currentProjectProgress(groupProgress) {
      const gp = groupProgress || {}
      if (this.effectiveScheduledMode !== 'delta') {
        return parseFloat(gp.progress_current_project) || 0
      }
      const fieldByPeriod = {
        today: 'progress_current_project_today_delta',
        week:  'progress_current_project_week_delta',
        month: 'progress_current_project_month_delta',
      }
      const field = fieldByPeriod[this.effectiveScheduledPeriod] || fieldByPeriod.today
      return Math.max(0, parseFloat(gp[field]) || 0)
    },

    // Como currentProjectProgress pero con las claves normalizadas 0-100 al grupo.
    currentGroupProgress(groupProgress) {
      const gp = groupProgress || {}
      if (this.effectiveScheduledMode !== 'delta') {
        return parseFloat(gp.progress_current_group) || 0
      }
      const fieldByPeriod = {
        today: 'progress_current_group_today_delta',
        week:  'progress_current_group_week_delta',
        month: 'progress_current_group_month_delta',
      }
      const field = fieldByPeriod[this.effectiveScheduledPeriod] || fieldByPeriod.today
      return Math.max(0, parseFloat(gp[field]) || 0)
    },

    // A origen: acumulado real ponderado al peso del grupo. Parcial: avance real del período
    // en curso (real ahora − baseline *_actual de progress_periods). Clamp a 0 porque un delta
    // puede ser negativo tras una corrección.
    realProjectProgress(groupProgress) {
      const gp = groupProgress || {}
      if (this.effectiveScheduledMode !== 'delta') {
        return parseFloat(gp.progress_real_project) || 0
      }
      const fieldByPeriod = {
        today: 'progress_real_project_today_delta',
        week:  'progress_real_project_week_delta',
        month: 'progress_real_project_month_delta',
      }
      const field = fieldByPeriod[this.effectiveScheduledPeriod] || fieldByPeriod.today
      return Math.max(0, parseFloat(gp[field]) || 0)
    },

    // Como realProjectProgress pero con las claves normalizadas 0-100 al grupo.
    realGroupProgress(groupProgress) {
      const gp = groupProgress || {}
      if (this.effectiveScheduledMode !== 'delta') {
        return parseFloat(gp.progress_real_group) || 0
      }
      const fieldByPeriod = {
        today: 'progress_real_group_today_delta',
        week:  'progress_real_group_week_delta',
        month: 'progress_real_group_month_delta',
      }
      const field = fieldByPeriod[this.effectiveScheduledPeriod] || fieldByPeriod.today
      return Math.max(0, parseFloat(gp[field]) || 0)
    },

    getIssueIcon(activityTaskStatus) {
      const taskStatusMap = {
        0: { icon: 'hand', color: 'text-orange' }, // WESTOP_ACTIVITY
        1: { icon: 'hand', color: 'text-red' }, // WESTOP_PROJECT
        2: { icon: 'exclamation-triangle', color: 'text-yellow' }, // ISSUE_PENDING
        3: { icon: 'exclamation-triangle', color: 'text-orange' }, // ISSUE_WITHOUT_LOCK
        4: { icon: 'exclamation-triangle', color: 'text-red' }, // ISSUE_LOCK_TASK
        5: { icon: 'hand', color: 'text-red' } // WESTOP_ACTIVITY_parent
      }

      return taskStatusMap[activityTaskStatus] || null
    },

    getIssueIcon(activityTaskStatus) {
      const taskStatusMap = {
        0: { icon: 'hand', color: 'text-orange' }, // WESTOP_ACTIVITY
        1: { icon: 'hand', color: 'text-red' }, // WESTOP_PROJECT
        2: { icon: 'exclamation-triangle', color: 'text-yellow' }, // ISSUE_PENDING
        3: { icon: 'exclamation-triangle', color: 'text-orange' }, // ISSUE_WITHOUT_LOCK
        4: { icon: 'exclamation-triangle', color: 'text-red' }, // ISSUE_LOCK_TASK
        5: { icon: 'hand', color: 'text-red' } // WESTOP_ACTIVITY_parent
      }

      return taskStatusMap[activityTaskStatus] || null
    },

    customizeFilters() {
      let filters = this.$refs.filtersRef.generateFilters();
      filters.with_assignments = 1;
      filters.order_field = this.orderField;
      filters.order_dir = this.orderDir;
      filters.per_page = this.perPage;
      //filters.s = 1; //con subactividades. Como se piden de todas formas al desplegar, se quitan de aqui para aligerar carga

      if (this.filterSubactivities) {
        filters.filter_subactivities = 1
      }

      return filters;
    },

    getActivityFilters(row) {
      const props = {
        p: this.project.id,
        with_assignments: 1,
        order_field: this.orderField,
        order_dir: this.orderDir,
        per_page: this.perPage,
        page: this.page,
        group_id: row.id
      }
      return props;
    },

    onCreateActivityClicked() {
      let url = '/api/v2/locations?all=1&p=' + this.project.id;
      self = this;
      axios.get(url)
        .then(function (response) {
          if (response.data && response.data.success && response.data.locations) {
            self.$router.push({name: 'activity.create'})
          } else {
            self.$notify.error('project_without_locations_create_activity')
          }
        });
    },

    displayKanban() {
      this.$router.push({name: 'project.activities.kanban'});
    },

    displayGantt() {
      this.$router.push({name: 'project.activities.gantt'});
    },

    async createProjectDossier() {
      //TO-DO: realizar llamada al generador de informes para el proyecto this.project.id
      return true
    },

    applyFilters(page) {
      if (this.depth === 0) {
        // Raíz: genera los filtros desde activity-filters + paginación.
        this.generatedFilters = this.customizeFilters();
        this.generatedFilters.page = (page <= this.last) ? page : 1;
        // actualiza en localstore la pagina actual
        // this.$store.dispatch('searchFilters/setActivitiesFilters', { page: this.generatedFilters.page });
        this.currentPage = this.generatedFilters.page;
      } else {
        // Sub-nivel: reutiliza los filtros calculados por la raíz para que el backend
        // recompute la misma "visibilidad de usuario" en todos los niveles del árbol.
        this.generatedFilters = { ...(this.filtersFromRoot || {}) };
      }

      this.generatedFilters.with_groups = 1

      // this.getActivitiesList(this.generatedFilters)
      this.getActivityGroupsList(this.generatedFilters)
    },

    fetchData() {
      if (this.page <= this.last) {
        this.isLoadingTable = true;
        this.applyFilters(this.page);
        this.isLoadingTable = false
      }
    },

    hasStatusList() {
      return this.childStatusList.length > 0;
      // return this.$refs.filtersRef.hasStatusList(); // da error por componente todavia no montado (ahora usamos emit-on)
    },

    goToPage(destiny = 'drawings') {

      switch (destiny) {

        case 'drawings':
            this.$router.push({ name: 'project.drawings' })
            break;
        case 'activities_list': // ver la vista de actividades en tabla
            this.$router.push({ name: 'project.activities' })
            break;
      }

    },

    colorGroup(group){
      let defaultColor = "#F29B57"
      let configs = JSON.parse(group.configs)

      return configs && configs.color ? configs.color : defaultColor
    },

    nameGroup(group){
      return group.name.substring(0, 2).toUpperCase()
    },

    toggle(row) {
      this.$refs.tbl_groups.toggleDetails(row)
    },

    onClick(params) {
      // console.log(params)
    },

    /**
     * Cambia la ventana a la tabla de actividades normal
     */
    displayTable() {
      this.$router.push({name: 'project.activities'});
    },

    // ordenacion natural para code (1.1, 1.2, ... 1.10)
    sortByCode(a, b, asc = true) {

      // opcion pasando array children groups
      if (Array.isArray(a)) {
        const arr = a;
        asc = (typeof b === 'boolean') ? b : true;
        return arr.slice().sort((x, y) => {
          const cmp = (x.code || '').localeCompare(y.code || '', undefined, { numeric: true, sensitivity: 'base' });
          return asc ? cmp : -cmp;
        });
      }

      // opcion parent groups
      const cmp = (a.code || '').localeCompare(b.code || '', undefined, { numeric: true, sensitivity: 'base' });
      return asc ? cmp : -cmp;
    },

    quickButtonMenu() {
      let extraButtons = []
      if (this.layoutGeneral) {
        extraButtons.push({
          name: 'layout_general',
          text: this.$t('layout_general'),
          showText: true,
          icon: 'map',
          visible: true,
          tooltip: true,
          bgClass: 'bg-blue',
          textClass: 'text-white',
          handleOnClick: () => { this.showLayoutGeneral() }
        })
      }
      return [
        ...extraButtons,
        // Vista agrupación de actividades
        {
          name: 'activity_grouping_view',
          text: this.$t('activities_list'),
          showText: true,
          icon: 'table',
          visible: !this.project.can_collaborate,
          tooltip: true,
          handleOnClick: () => { this.goToPage('activities_list') }
        },

        // Crear actividad
        {
          name: 'create_activity',
          text: this.$t('add_activity'),
          showText: true,
          icon: 'plus',
          visible: this.project.can_admin,
          tooltip: true,
          handleOnClick: this.onCreateActivityClicked
        },
        // Botón para abrir diagrama de gantt
        {
          name: 'gantt_chart_view',
          text: this.$t('display_gantt'),
          showText: false,
          icon: 'chart-gantt',
          visible: !this.project.can_collaborate,
          tooltip: true,
          handleOnClick: this.displayGantt
        },
        // Botón para ver Kanban
        {
          name: 'kanban_view',
          text: this.$t('display_kanban'),
          showText: false,
          icon: 'columns',
          visible: this.project.can_admin,
          tooltip: true,
          handleOnClick: this.displayKanban
        },
        // Botón para ver Carpetas documentos
        {
          name: 'document_folder_view',
          text: this.$t('display_folders'),
          showText: false,
          icon: 'folder-plus',
          visible: true,
          tooltip: true,
          handleOnClick: () => { this.$router.push({name: 'project.activities.folders'}); }
        },
        // Exportar a Excel el estado desplegado (expandido) actual.
        // Solo visible cuando hay algo desplegado en el árbol, y con color
        // destacado (naranja) para diferenciarlo del resto de iconos.
        {
          name: 'export_expanded_groups',
          text: this.$t('export_expanded_groups'),
          showText: false,
          icon: 'file-export',
          visible: !this.project.can_collaborate && this.effectiveGroupsPath.length > 0,
          tooltip: true,
          bgClass: 'bg-orange',
          textClass: 'text-white',
          handleOnClick: this.exportExpandedGroups
        }
      ]
    },

    async exportExpandedGroups() {
      try {
        this.$notify.success('generating_report')
        const { data } = await axios.get(
          `/api/v2/project/${this.project.id}/activity-groups/export-expanded`,
          {
            params: {
              path:   this.effectiveGroupsPath,
              period: this.effectiveScheduledPeriod,
              mode:   this.effectiveScheduledMode,
            },
          }
        )
        if (data && data.success && data.url) {
          window.open(data.url)
          this.$notify.success('report_generated')
        } else {
          this.$notify.error('error_loading_report')
        }
      } catch (e) {
        this.$notify.error('error_loading_report')
      }
    },

  },

};
</script>

<style lang="scss">
.disabled-subactivity {
  background: #F8FAFC !important;
  color: #B8C2CA !important;
}

.reduce {
  transform: scale(0.80)
}

.vertical-center {
  vertical-align: inherit !important;
}

/* Se le debería de poder cambiar el icono al botón de details, no probado */
.sub-notifications > td.chevron-cell {
  background-color: #F8E0E0 !important;
}

.sub-notifications > td.chevron-cell svg {
  color: #e3342f !important;
}

.subactivities {
  .table th {
    background-color: #89a4d6;
  }

  &.b-table .table.is-bordered th.is-current-sort, &.b-table .table.is-bordered th.is-sortable:hover {
    background-color: #89a4d6 !important;
  }
}
</style>


